export type FilterSectionName = 'period' | 'classification' | 'content' | 'interactions' | 'conversations' | 'context' | 'author';
export type ConversationFieldName = 'channels' | 'pages' | 'interactions' | 'criticality' | 'status' | 'respondent' | 'routing' | 'conversationId' | 'publicationType';
export type ChipName = 'open-sea' | 'proprietary';
export type MfeMode = 'full-filter' | 'date-only' | 'date-and-conversations' | 'restricted';

export interface ResolvedFilterConfig {
  mfeMode: MfeMode;
  visibleSections: ReadonlyArray<FilterSectionName>;
  visibleConversationFields: ReadonlyArray<ConversationFieldName>;
  allowedChips: ReadonlyArray<ChipName>;
  hideChipsRow: boolean;
  hideZoom: boolean;
  hideSavedFilters: boolean;
  disableClassificationGroups: boolean;
  disableContentStilingueEyes: boolean;
  disableContentIncludeMentions: boolean;
  disableOriginChannels: boolean;
  disableAuthorFollowers: boolean;
  disableOtherConversationFields: boolean;
  showPublicationTypeInContent: boolean;
  lockPeriodTo24h: boolean;
}

type FilterPreset = Omit<
  ResolvedFilterConfig,
  'mfeMode' | 'disableClassificationGroups' | 'disableContentStilingueEyes' | 'disableContentIncludeMentions' | 'disableOriginChannels' | 'disableAuthorFollowers' | 'disableOtherConversationFields' | 'showPublicationTypeInContent' | 'lockPeriodTo24h'
>;

const ALL_SECTIONS: ReadonlyArray<FilterSectionName> = ['period', 'classification', 'content', 'interactions', 'conversations', 'context', 'author'];
const ALL_CONVERSATION_FIELDS: ReadonlyArray<ConversationFieldName> = ['channels', 'pages', 'interactions', 'criticality', 'status', 'respondent', 'routing', 'conversationId', 'publicationType'];
const ALL_CHIPS: ReadonlyArray<ChipName> = ['open-sea', 'proprietary'];

const isProprietaryOnlyFullFilter = (mode: MfeMode, chips: ReadonlyArray<ChipName>): boolean =>
  mode === 'full-filter' && chips.length === 1 && chips.includes('proprietary');

export const isMetricsFieldRestrictedContext = (mode: MfeMode, chips: ReadonlyArray<ChipName>): boolean =>
  mode === 'restricted' || isProprietaryOnlyFullFilter(mode, chips);

export const PRESETS: Record<MfeMode, FilterPreset> = {
  'full-filter': {
    visibleSections: ALL_SECTIONS,
    visibleConversationFields: ALL_CONVERSATION_FIELDS,
    allowedChips: ALL_CHIPS,
    hideChipsRow: false,
    hideZoom: false,
    hideSavedFilters: false,
  },
  'date-only': {
    visibleSections: ['period'],
    visibleConversationFields: [],
    allowedChips: [],
    hideChipsRow: true,
    hideZoom: true,
    hideSavedFilters: true,
  },
  'date-and-conversations': {
    visibleSections: ['period', 'conversations'],
    visibleConversationFields: ['status', 'respondent'],
    allowedChips: [],
    hideChipsRow: true,
    hideZoom: true,
    hideSavedFilters: true,
  },
  'restricted': {
    visibleSections: ['period', 'classification', 'content', 'interactions', 'context'],
    visibleConversationFields: [],
    allowedChips: ['proprietary'],
    hideChipsRow: false,
    hideZoom: false,
    hideSavedFilters: false,
  },
};

const withDerivedRestrictions = (
  mfeMode: MfeMode,
  preset: FilterPreset,
  allowedChips: ReadonlyArray<ChipName>,
  hideZoom: boolean,
  lockPeriodTo24h = false,
): ResolvedFilterConfig => {
  const restrictedContext = isMetricsFieldRestrictedContext(mfeMode, allowedChips);
  const isOpenSeaOnly = mfeMode === 'full-filter' && allowedChips.length === 1 && allowedChips.includes('open-sea');
  const visibleSections = isOpenSeaOnly
    ? preset.visibleSections.filter((s) => s !== 'conversations')
    : preset.visibleSections;
  const visibleConversationFields = isOpenSeaOnly
    ? preset.visibleConversationFields.filter((f) => f !== 'publicationType')
    : preset.visibleConversationFields;
  return {
    ...preset,
    mfeMode,
    allowedChips,
    hideZoom,
    visibleSections,
    visibleConversationFields,
    disableClassificationGroups: restrictedContext,
    disableContentStilingueEyes: restrictedContext,
    disableContentIncludeMentions: restrictedContext,
    disableOriginChannels: restrictedContext,
    disableAuthorFollowers: isProprietaryOnlyFullFilter(mfeMode, allowedChips),
    disableOtherConversationFields: mfeMode === 'date-and-conversations',
    showPublicationTypeInContent: isOpenSeaOnly,
    lockPeriodTo24h,
  };
};

export const resolveConfigFromDOM = (): ResolvedFilterConfig => {
  const headerEl = document.querySelector('content-header');
  const isDefaultMode = headerEl?.getAttribute('filter-default-mode') === 'true';
  const lockPeriodTo24h = headerEl?.getAttribute('filter-fixed-24h') === 'true';

  if (!headerEl || isDefaultMode) {
    const fallbackPreset = PRESETS['full-filter'];
    return withDerivedRestrictions('full-filter', fallbackPreset, fallbackPreset.allowedChips, fallbackPreset.hideZoom, lockPeriodTo24h);
  }

  const isDateOnly = headerEl.getAttribute('filter-date-only') === 'true';
  const isDateAndConv = headerEl.getAttribute('filter-date-and-conversations') === 'true';
  const isRestricted = headerEl.getAttribute('filter-restricted') === 'true';
  const isProprietary = headerEl.getAttribute('filter-proprietary-only') === 'true';
  const isOpenSea = headerEl.getAttribute('filter-open-sea-only') === 'true';
  const hideZoomAttr = headerEl.getAttribute('hide-zoom-button') === 'true';

  // Determine allowed chips FIRST - these have priority over mode flags
  const resolveAllowedChips = (): ReadonlyArray<ChipName> => {
    if (isProprietary) return ['proprietary'];
    if (isOpenSea) return ['open-sea'];
    // Will use preset.allowedChips as fallback after determining mfeMode
    return [];
  };

  // Determine mfeMode - open-sea/proprietary always use full-filter
  const resolveMfeMode = (): MfeMode => {
    if (isProprietary || isOpenSea) return 'full-filter';
    if (isDateOnly) return 'date-only';
    if (isDateAndConv) return 'date-and-conversations';
    if (isRestricted) return 'restricted';
    return 'full-filter';
  };

  const mfeMode = resolveMfeMode();
  const preset = PRESETS[mfeMode];

  const allowedChips = resolveAllowedChips().length > 0 
    ? resolveAllowedChips()
    : preset.allowedChips;

  return withDerivedRestrictions(mfeMode, preset, allowedChips, hideZoomAttr || preset.hideZoom, lockPeriodTo24h);
};